<template>
  <Section id="integrations" tone="inverse" pad-y="lg" aria-labelledby="integrations-heading">
    <Container>
      <div class="mb-10 flex flex-col gap-3">
        <h2 id="integrations-heading" class="text-3xl font-bold text-ink-inverse md:text-4xl">
          {{ t('sections.integrations.title') }}
        </h2>
        <p class="max-w-2xl text-base text-ink-inverse/80 md:text-lg">
          {{ t('sections.integrations.subtitle') }}
        </p>
      </div>

      <Grid :cols="{ base: 1, md: 2 }" :gap="6" class="mb-10">
        <Card
          v-for="scenario in scenarios"
          :key="scenario.id"
          tone="muted"
          padding="lg"
          class="flex flex-col gap-3"
        >
          <h3 class="text-lg font-bold text-ink-strong md:text-xl">{{ scenario.title }}</h3>
          <p class="text-sm text-ink md:text-base">{{ scenario.description }}</p>
          <ul class="mt-2 flex flex-col gap-1.5 text-sm text-ink">
            <li v-for="b in scenario.bullets" :key="b" class="flex items-start gap-2">
              <BaseIcon name="check" :size="14" class="mt-1 shrink-0 text-accent" />
              <span>{{ b }}</span>
            </li>
          </ul>
        </Card>
      </Grid>

      <Grid :cols="{ base: 1, md: 2, lg: 3 }" :gap="3" class="mb-10">
        <IntegrationCard
          v-for="int in integrations"
          :key="int.id"
          :name="int.name"
          :protocol="int.protocol"
          :icon="int.icon"
          tone="inverse"
        />
      </Grid>

      <Card tone="accent" padding="lg" class="bg-accent-soft/10 text-ink-inverse">
        <h3 class="mb-3 text-base text-white font-bold md:text-lg">
          {{ t('sections.integrations.outcomesTitle') }}
        </h3>
        <ul class="grid gap-2 md:grid-cols-3">
          <li v-for="item in outcomes" :key="item" class="flex items-start gap-2 text-sm">
            <BaseIcon name="check" :size="16" class="mt-0.5 shrink-0 text-accent" />
            <span>{{ item }}</span>
          </li>
        </ul>
      </Card>
    </Container>
  </Section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Section from '@/components/primitives/Section.vue';
import Container from '@/components/primitives/Container.vue';
import Grid from '@/components/primitives/Grid.vue';
import Card from '@/components/primitives/Card.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import IntegrationCard from '@/components/domain/IntegrationCard.vue';
import {
  DEPLOYMENT_SCENARIOS,
  INTEGRATIONS,
  INTEGRATIONS_OUTCOMES,
  INTEGRATIONS_OUTCOMES_EN,
  type DeploymentItem,
  type IntegrationItem,
} from '@/content/home-data';
import { useLocale } from '@/composables/useLocale';
import { pickLocalized, pickLocalizedArray } from '@/lib/i18n/localize';

const { t } = useI18n();
const { locale } = useLocale();

const scenarios = computed<readonly DeploymentItem[]>(() =>
  DEPLOYMENT_SCENARIOS.map((s) => ({
    ...s,
    title: pickLocalized(s, 'title', locale.value),
    description: pickLocalized(s, 'description', locale.value),
    bullets: pickLocalizedArray(s, 'bullets', locale.value),
  })),
);

const integrations = computed<readonly IntegrationItem[]>(() =>
  INTEGRATIONS.map((i) => ({
    ...i,
    name: pickLocalized(i, 'name', locale.value),
    protocol: pickLocalized(i, 'protocol', locale.value),
  })),
);

const outcomes = computed(() =>
  locale.value === 'en' ? INTEGRATIONS_OUTCOMES_EN : INTEGRATIONS_OUTCOMES,
);
</script>
